import axios from '@/plugins/axios';
import qs from 'qs';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { limit: 20, ordering: 'name' };

const state = {
    customers: [],
    customers_count: 0,
    customers_filters: DEFAULT_FILTERS,
    customers_loading: false,
    customers_loading_errors: null,
    customers_cancel_source: null,

    synchronizing_customers: false,
    synchronizing_customers_errors: {},

    // New state for map customers
    map_customers: [],
    map_customers_loading: false,
    map_customers_loading_errors: null,
    map_customers_debounce_timer: null,

    // Geographic filters state
    geo_filters: null,

    // Bulk actions state
    selected_items: [],
    bulk_action_loading: false,
};

const mutations = {
    updateCustomers(state, customers) {
        state.customers = customers;
        // Replace selected_items instances with newly fetched customers based on id field
        state.selected_items = state.selected_items?.map((selected_item) => {
            return customers.find((customer) => customer.id === selected_item.id) || selected_item;
        }) || [];
    },
    updateCustomersCount(state, count) {
        state.customers_count = count;
    },
    updateCustomersLoading(state, loading) {
        state.customers_loading = loading;
    },
    updateCustomersLoadingErrors(state, errors) {
        state.customers_loading_errors = errors;
    },
    updateCustomersFilters(state, filters) {
        state.customers_filters = filters;
        saveFiltersToStorage(getStorageKey('customers', 'default'), state.customers_filters);
    },
    updateCustomersCancelSource(state, source) {
        state.customers_cancel_source = source;
    },
    updateSynchronizingCustomers(state, synchronizing) {
        state.synchronizing_customers = synchronizing;
    },
    updateSynchronizingCustomersErrors(state, errors) {
        state.synchronizing_customers_errors = errors;
    },

    // New mutations for map customers
    updateMapCustomers(state, customers) {
        state.map_customers = customers;
    },
    updateMapCustomersLoading(state, loading) {
        state.map_customers_loading = loading;
    },
    updateMapCustomersLoadingErrors(state, errors) {
        state.map_customers_loading_errors = errors;
    },
    updateMapCustomersDebounceTimer(state, timer) {
        state.map_customers_debounce_timer = timer;
    },
    updateGeoFilters(state, geo_filters) {
        state.geo_filters = geo_filters;
    },
    updateSelectedItems(state, selected_items) {
        state.selected_items = selected_items || [];
    },
    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = loading;
    },
};

const actions = {

    exportCustomersAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.customers_filters);
            if (state.geo_filters) {
                const bounds = state.geo_filters;
                filters.geolocation = `${bounds.southwest.lat},${bounds.northeast.lat},${bounds.southwest.lng},${bounds.northeast.lng}`;
            }
            filters.offset = null;
        }
        if (state.customers_filters.prospect) {
            filters.prospect = true;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/customers/",
            params: filters,
        }, $utils.getFilename('customers'));
    },

    async synchronizeCustomers({ commit, dispatch, state }, params) {
        commit('updateSynchronizingCustomers', true);
        commit('updateSynchronizingCustomersErrors', null);

        let url = '/api/sync/customers/';

        try {
            const response = await axios.post(url, { payload: params.payload });
            commit('updateSynchronizingCustomers', false);
            return response.data.payload;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            error.details = error.details.payload;
            commit('updateSynchronizingCustomers', false);
            commit('updateSynchronizingCustomersErrors', error.details);
            throw error;
        }
    },

    async fetchCustomers({ commit, dispatch, state }, params) {
        commit('updateCustomersLoading', true);
        commit('updateCustomersLoadingErrors', null);

        if (state.customers_cancel_source) {
            state.customers_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateCustomersCancelSource', cancelSource);

        let filters = { ...loadFiltersFromStorage(getStorageKey('customers', 'default'), DEFAULT_FILTERS)};
        delete filters.geolocation;

        try {
            const apiFilters = { ...filters };
            // Merge regular filters with geographic filters if they exist
            if (state.geo_filters) {
                const bounds = state.geo_filters;
                // New API format: geolocation parameter with 4 comma-separated values
                // Format: lat_min,lat_max,lon_min,lon_max
                apiFilters.geolocation = `${bounds.southwest.lat},${bounds.northeast.lat},${bounds.southwest.lng},${bounds.northeast.lng}`;
            }
            commit('updateCustomersFilters', filters);
            
            const response = await axios.get('/api/customers/', {
                params: apiFilters,
                cancelToken: state.customers_cancel_source?.token,
            });
            commit('updateCustomersLoading', false);
            commit('updateCustomers', response.data.results);
            commit('updateCustomersCount', response.data.count);
            commit('updateCustomersCancelSource', null);

            // Only trigger map customers fetch if no geo filters are active
            // (to avoid double fetching when geo filters are already applied)
                dispatch('fetchCustomersForMapDebounced');

            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            
            commit('updateCustomersLoading', false);
            commit('updateCustomersLoadingErrors', error.details);
            throw error;
        }
    },

    async fetchCustomersForMap({ commit, dispatch, state }, params) {
        commit('updateMapCustomersLoading', true);
        commit('updateMapCustomersLoadingErrors', null);

        try {
            // Use the same filters as regular customers fetch, but with map-specific overrides
            const mapFilters = {
                ...state.customers_filters,
                limit: 1000,
                geocoding_status: 'success',
                offset: 0 // Always start from beginning for map
            };

            // Add geographic bounds filtering if provided
            if (state.geo_filters) {
                const bounds = state.geo_filters;
                // New API format: geolocation parameter with 4 comma-separated values
                // Format: lat_min,lat_max,lon_min,lon_max
                mapFilters.geolocation = `${bounds.southwest.lat},${bounds.northeast.lat},${bounds.southwest.lng},${bounds.northeast.lng}`;
            }

            const response = await axios.get('/api/customers/', {
                params: mapFilters
            });
            // Transform customers data for map display
            const mapCustomers = response.data.results
                .filter(customer =>
                    customer.billing_address &&
                    customer.billing_address.latitude &&
                    customer.billing_address.longitude
                )
                .map(customer => ({
                    id: customer.id,
                    name: customer.name,
                    type: customer.type || 'customer',
                    address: customer.billing_address?.full || '',
                    position: {
                        lat: parseFloat(customer.billing_address.latitude),
                        lng: parseFloat(customer.billing_address.longitude)
                    },
                    // Raw data for infoTexts generation in components
                    rawData: {
                        type: customer.type,
                        name: customer.name,
                        code: customer.code,
                        address: customer.billing_address?.full,
                        phone: customer.billing_address?.phone_number,
                        emails: customer.billing_address?.emails,
                        connected: customer.entity
                    }
                }));
            commit('updateMapCustomers', mapCustomers);
            commit('updateMapCustomersLoading', false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateMapCustomersLoading', false);
            commit('updateMapCustomersLoadingErrors', error.details);
            throw error;
        }
    },

    fetchCustomersForMapDebounced({ commit, dispatch, state }, params) {
        // Clear existing timer if any
        if (state.map_customers_debounce_timer) {
            clearTimeout(state.map_customers_debounce_timer);
        }

        // Set new timer
        const timer = setTimeout(() => {
            dispatch('fetchCustomersForMap', params);
            commit('updateMapCustomersDebounceTimer', null);
        }, 1000); // 1 seconds delay

        commit('updateMapCustomersDebounceTimer', timer);
    },

    updateGeoFiltersAndRefresh({ commit, dispatch }, geo_filters) {
        commit('updateGeoFilters', geo_filters);
        // Only fetch customers - this will automatically apply geo filters
        // and won't trigger the automatic map fetch since geo_filters is now set
        dispatch('fetchCustomers');
    },

    clearGeoFiltersAndRefresh({ commit, dispatch }) {
        commit('updateGeoFilters', null);
        // Refresh customers - this will clear geo filters and trigger map fetch automatically
        dispatch('fetchCustomers');
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/customers/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch("fetchCustomers");
            dispatch("session/fetchStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};